import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import clsx from 'clsx';
import {
  InteractiveFormFieldValueDTO,
  VisitNoteDTO,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import {
  FormWorkflowStatus,
} from 'dtos/formWorkflowStatus';
import {
  useNotify,
} from 'core/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  VisitNoteContent,
} from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/PastNotes/PastNoteDetails';
import FillableForm from 'views/EMR/components/FillableForm';
import {
  PreparationCompleteData,
} from 'views/EMR/components/FillableForm/components/PreparationSteps';
import {
  ClinicianFormStepDialogProps,
} from 'views/EMR/components/FormWorkflow/types';
import {
  useCompleteClinicianFormStep,
  useClinicianFormRenderingMetadata,
} from 'views/EMR/components/FormWorkflow/hooks';
import {
  formRequiresClinicianReview,
  getFormWorkflowFromTransitionResponse,
  getFormWorkflowPatientMultiMediaIds,
  isOrderOrReferralCategory,
} from 'views/EMR/components/FormWorkflow/utils';
import {
  serializeSessionData,
} from 'views/EMR/components/FillableForm/utils/serializeSessionData';
import {
  mergeSharedPrepValues,
} from 'views/EMR/components/FillableForm/utils/signaturePrep';
import {
  useFormWorkflowDetail,
} from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/hooks';
import {
  useGetVisitNote,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import {
  Button,
} from '@populateui/ui';
import {
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import {
  visitNoteFormEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  formatDate,
} from 'utils/date';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import Name from 'pages/Dashboard/components/Name';
import Tag from 'core/components/Tag';
import EyeIcon from 'core/icons/eye';

type VisitNotePanelProps = {
  encounterId: number;
  isResolvingEncounter: boolean;
  isVisible: boolean;
  patient?: PatientCompact | null;
  className?: string;
};

function VisitNotePanel({
  encounterId,
  isResolvingEncounter,
  isVisible,
  patient,
  className,
}: VisitNotePanelProps) {
  const {
    data: visitNote,
    isLoading: isVisitNoteLoading,
  } = useGetVisitNote(isResolvingEncounter ? 0 : encounterId);

  const isLoading = isResolvingEncounter
    || (!isNilOrZero(encounterId) && isVisitNoteLoading);

  const visitDateLabel = notEmptyString(visitNote?.visitDateTime)
    ? formatDate(visitNote?.visitDateTime ?? '')
    : '';
  const typeOfVisitDescription = visitNote?.typeOfVisitDescription ?? '';
  const hasVisitMeta = notEmptyString(visitDateLabel)
    || notEmptyString(typeOfVisitDescription);
  const dateOfBirthLabel = notEmptyString(patient?.formattedDateOfBirth)
    ? (patient?.formattedDateOfBirth ?? '')
    : formatDate(patient?.dateOfBirth ?? '');
  const patientNameWithDob = compact([
    getDisplayName(patient?.formattedNames),
    dateOfBirthLabel,
  ]).join(' • ');
  const showEmptyVisitNote = !isLoading && isNilOrZero(visitNote?.visitNoteId);

  return (
    <aside className={clsx('w-[25rem] 2xl:w-[32rem] shrink-0 border-l border-gray-200 bg-gray-50 overflow-y-auto h-full', className)}>
      <div className="px-4 py-3 border-b border-gray-200 flex flex-col gap-2">
        {!isNil(patient) && (
          <Name
            initials={patient?.formattedNames?.initials}
            name={patientNameWithDob}
            image={{
              size: 'sm',
              variant: 'rounded',
            }}
            className="overflow-hidden"
          >
            {hasVisitMeta && (
              <div className="text-xs text-gray-600 truncate flex items-center gap-1">
                {notEmptyString(typeOfVisitDescription) && (
                  <Tag className="max-w-[250px] truncate" tagName={typeOfVisitDescription} />
                )}
                {notEmptyString(visitDateLabel) && notEmptyString(typeOfVisitDescription) && (
                  <span> • </span>
                )}
                {notEmptyString(visitDateLabel) && (
                  <span>
                    Visited on
                    {' '}
                    <span className="font-semibold">{visitDateLabel}</span>
                  </span>
                )}
              </div>
            )}
          </Name>
        )}
      </div>
      {isVisible && (
        showEmptyVisitNote ? (
          <div className="px-4 py-2 text-xs text-gray-500">
            No visit note linked
          </div>
        ) : (
          <VisitNoteContent
            visitNote={visitNote as VisitNoteDTO | undefined}
            isLoading={isLoading}
            hideApply
          />
        )
      )}
    </aside>
  );
}

export default function ClinicianFormStepDialog({
  formWorkflowId,
  category,
  patient,
  encounterId: encounterIdProp,
  onClose,
}: ClinicianFormStepDialogProps) {
  const notify = useNotify();
  const [formIndex, setFormIndex] = useState(0);
  const [accumulatedValues, setAccumulatedValues] = useState<InteractiveFormFieldValueDTO[]>([]);
  const [includeVisitNoteForFormIds, setIncludeVisitNoteForFormIds] = useState<number[]>([]);
  const [sharedPrepValues, setSharedPrepValues] = useState<PreparationCompleteData | null>(null);
  const [isVisitNotePanelVisible, setIsVisitNotePanelVisible] = useState(true);
  const handleSharedPrepComplete = useCallback((data: PreparationCompleteData) => {
    setSharedPrepValues((previous) => mergeSharedPrepValues(previous, data));
  }, []);

  const {
    data: workflowDetail,
    isLoading: isWorkflowDetailLoading,
    isError: isWorkflowDetailError,
  } = useFormWorkflowDetail(
    !isNilOrZero(formWorkflowId) ? formWorkflowId : 0,
  );

  const categoryByFormId = useMemo(
    () => new Map(
      (workflowDetail?.formWorkflow?.allocations ?? []).map((allocation) => [
        allocation.formId ?? 0,
        allocation.category,
      ]),
    ),
    [workflowDetail?.formWorkflow?.allocations],
  );

  const resolvedEncounterId = encounterIdProp
    ?? workflowDetail?.formWorkflow?.encounter?.encounterId
    ?? 0;

  const {
    data: renderingMetadata,
    isLoading: isRenderingMetadataLoading,
    isError: isRenderingMetadataError,
  } = useClinicianFormRenderingMetadata(
    formWorkflowId,
    FormFieldAssignee.Clinician,
    !isNilOrZero(formWorkflowId),
  );

  const {
    mutateAsync: completeClinicianStep,
    isPending: isCompleting,
  } = useCompleteClinicianFormStep();

  const reviewableMetadataEntries = useMemo(
    () => (renderingMetadata?.renderingData ?? []).filter((entry) => formRequiresClinicianReview(
      entry.fields ?? [],
      categoryByFormId.get(entry.formId ?? 0) ?? category,
    )),
    [category, categoryByFormId, renderingMetadata?.renderingData],
  );

  const reviewableFormIdsKey = useMemo(
    () => reviewableMetadataEntries.map((entry) => entry.formId).join(','),
    [reviewableMetadataEntries],
  );

  const currentMetadata = reviewableMetadataEntries[formIndex] ?? null;
  const currentFormCategory = categoryByFormId.get(currentMetadata?.formId ?? 0) ?? category;
  const supportsVisitNotePanel = isOrderOrReferralCategory(currentFormCategory);
  const visitNoteToggleLabel = isVisitNotePanelVisible ? 'Hide visit note' : 'Show visit note';

  const session = useMemo(
    () => (currentMetadata ? serializeSessionData(currentMetadata) : null),
    [currentMetadata],
  );

  const isResolvingEncounter = isNilOrZero(encounterIdProp) && isWorkflowDetailLoading;
  const isPageLoading = isRenderingMetadataLoading
    || isWorkflowDetailLoading
    || isCompleting;

  useEffect(() => {
    setFormIndex(0);
  }, [reviewableFormIdsKey]);

  useEffect(() => {
    if (isRenderingMetadataError || isWorkflowDetailError) {
      onClose();
    }
  }, [isRenderingMetadataError, isWorkflowDetailError, onClose]);

  useEffect(() => {
    if (isPageLoading || reviewableMetadataEntries.length > 0) {
      return;
    }

    notify({
      variant: 'error',
      message: 'No forms are available for clinician review.',
    });
    onClose();
  }, [isPageLoading, notify, onClose, reviewableMetadataEntries.length]);

  const handleWorkflowComplete = useCallback(async ({
    interactiveFieldValues,
    includeVisitNote = false,
    formId,
  }: {
    interactiveFieldValues: InteractiveFormFieldValueDTO[];
    includeVisitNote?: boolean;
    formId?: number;
  }) => {
    const mergedValues = [...accumulatedValues, ...interactiveFieldValues];
    const nextIncludeVisitNoteForFormIds = includeVisitNote && !isNilOrZero(formId)
      ? [...new Set([...includeVisitNoteForFormIds, formId as number])]
      : includeVisitNoteForFormIds;
    const isLastForm = formIndex >= reviewableMetadataEntries.length - 1;

    if (!isLastForm) {
      setAccumulatedValues(mergedValues);
      setIncludeVisitNoteForFormIds(nextIncludeVisitNoteForFormIds);
      setFormIndex((current) => current + 1);
      return;
    }

    try {
      const response = await completeClinicianStep({
        formWorkflowId,
        interactiveFieldValues: mergedValues,
        includeVisitNote: false,
        includeVisitNoteForFormIds: nextIncludeVisitNoteForFormIds,
      });

      const workflow = getFormWorkflowFromTransitionResponse(response);

      if (workflow?.status === FormWorkflowStatus.AwaitingPatient) {
        notify({
          variant: 'success',
          message: response.successMessage ?? 'Clinician section completed. Forms sent to patient.',
        });
        onClose();
        return;
      }

      const mediaIds = workflow
        ? getFormWorkflowPatientMultiMediaIds(workflow)
        : [];
      if (mediaIds.length > 0) {
        const previewCategory = categoryByFormId.get(
          reviewableMetadataEntries[reviewableMetadataEntries.length - 1]?.formId ?? 0,
        ) ?? category;

        visitNoteFormEventChannel.emit('onOpenVisitNoteFormPreview', {
          patient,
          patientMultiMediaIds: mediaIds,
          includeVisitNote: nextIncludeVisitNoteForFormIds.length > 0,
          category: previewCategory,
        });
      }

      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to complete clinician step';
      notify({ message, variant: 'error' });
      throw error;
    }
  }, [
    accumulatedValues,
    category,
    completeClinicianStep,
    formIndex,
    includeVisitNoteForFormIds,
    categoryByFormId,
    reviewableMetadataEntries,
    notify,
    onClose,
    patient,
    formWorkflowId,
  ]);

  return (
    <Dialog
      open={!isNilOrZero(formWorkflowId)}
      onClose={onClose}
      closeBtnClasses="!hidden"
      fullWidth
      maxWidth={false}
      classes={{
        root: '!w-full !h-full flex flex-col !p-0 !space-y-0 fillable-form-dialog',
        dialog: 'hidden-backdrop populate-iframe-modal !m-0 settings-form-dialog',
        bodyRoot: '!w-screen !m-0 !max-w-[calc(100%-12px)] !h-[calc(100vh-24px)]',
        container: '!justify-start',
      }}
    >
      <div className="flex flex-col h-full min-h-0">
        <div className="flex flex-row justify-between items-center px-4 py-2 border-b border-gray-200">
          <div className="font-semibold text-lg truncate">
            Complete
            {' '}
            {currentFormCategory.name}
            {reviewableMetadataEntries.length > 1
              ? ` (${formIndex + 1}/${reviewableMetadataEntries.length})`
              : ''}
          </div>
          <div className="flex items-center gap-2">
            {supportsVisitNotePanel && (
              <Button
                variant="outlined"
                size="small"
                disabled={isCompleting}
                onClick={() => { setIsVisitNotePanelVisible((visible) => !visible); }}
                className="!h-6 shrink-0"
              >
                <EyeIcon className="!w-4 !h-4 mr-2 !text-blue-500" />
                {visitNoteToggleLabel}
              </Button>
            )}
            <IconButton onClick={onClose} aria-label="Close" disabled={isCompleting}>
              <CloseIcon className="!w-5 !h-5" />
            </IconButton>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden relative">
          <div className={clsx('h-full min-h-0', { flex: isVisitNotePanelVisible && supportsVisitNotePanel })}>
            <div className={clsx('h-full min-h-0 overflow-hidden relative', {
              'flex-1 min-w-0': isVisitNotePanelVisible && supportsVisitNotePanel,
            })}
            >
              {isPageLoading && (
                <div className="flex h-full w-full items-center justify-center">
                  <Spinner />
                </div>
              )}
              {!isPageLoading && !isNil(session) && (
                <FillableForm
                  session={session}
                  category={currentFormCategory}
                  onClose={onClose}
                  isSubmitting={isCompleting}
                  sharedPrepValues={sharedPrepValues}
                  onSharedPrepComplete={handleSharedPrepComplete}
                  workflow={{
                    formWorkflowId,
                    isLastForm: formIndex >= reviewableMetadataEntries.length - 1,
                    onComplete: handleWorkflowComplete,
                  }}
                />
              )}
            </div>
            {supportsVisitNotePanel && (
              <VisitNotePanel
                encounterId={resolvedEncounterId}
                isResolvingEncounter={isResolvingEncounter}
                isVisible={isVisitNotePanelVisible}
                patient={patient}
                className={clsx({ hidden: !isVisitNotePanelVisible })}
              />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
